import React, { Fragment, useEffect, useRef, useState } from "react";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, Grid, TextField,
} from "@mui/material";
import { Typography } from "@mui/material";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import LockIcon from "@mui/icons-material/Lock";
import CreditCardOffIcon from "@mui/icons-material/CreditCardOff";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import RefreshIcon from "@mui/icons-material/Refresh";
import SpeedIcon from "@mui/icons-material/Speed";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import HttpService from "../../services/HttpService";
import UserService from "../../services/UserService";
import CRMTextField, {
  currencyFormat,
  inputNumericMask,
} from "../../components/crm/CRMTextField";
import { PendingTransactions } from "../../components/ManageCard";
import Labels from "../../labels/Labels";

// Format date for display
const formatDateForDisplay = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
};

export default function CardView({
  changeParentTo,
  idCustomer,
  handleCreditClick,
  handleDebitClick,
  handleVelocityClick,
  showAdminButtons,
}) {
  const queryClient = useQueryClient();
  const [loadingFields, setLoadingFields] = useState([]);
  const [action, setAction] = React.useState();
  const [showDeactive, setShowDeactive] = React.useState(false);
  const [showActive, setShowActive] = React.useState(false);
  const [showRefresh, setShowRefresh] = React.useState(false);
  const [refreshDisabled, setRefreshDisabled] = useState(false);
  const refreshTimeout = useRef();
  const [deactivationOpen, setDeactivationOpen] = useState(false);
  const [confirmationName, setConfirmationName] = useState("");

  const cardholderQuery = useQuery(
    ["cardholder", idCustomer],
    async () => {
      const response = await HttpService.getAxiosClient().get(
        window.API_URL + `/member/${idCustomer}`
      );
      return response.data;
    },
    { enabled: deactivationOpen }
  );
  const fullName = cardholderQuery.data?.name;
  const expectedName = typeof fullName?.value === "string" && !fullName.locked
    ? fullName.value.trim() : "";

  // #region Monthly Payment Limit
  const cardHolderMutation = useMutation(
    (body) => {
      return HttpService.getAxiosClient().post(
        window.API_URL + `/member/admin-card`,
        body
      );
    },
    {
      onSuccess: (_response, body) => {
        queryClient.invalidateQueries(["cardview", idCustomer]);
        if (body.status === "CLOSE") {
          setDeactivationOpen(false);
          setConfirmationName("");
        }
      },
      onError: (err) => {
        setLoadingFields((fields) =>
          fields.filter(
            (field) =>
              field !== "monthlyPaymentLimit" && field !== "yearlyPaymentLimit"
          )
        );
        console.error(err);
      },
    }
  );

  useEffect(() => {
    setDeactivationOpen(false);
    setConfirmationName("");
  }, [idCustomer]);

  const newMonthlyPaymentLimitSave = (newMonthlyPaymentLimit) => {
    if (!newMonthlyPaymentLimit) {
      console.error("No new monthly payment limit provided");
      return;
    }
    setLoadingFields((fields) => [...fields, "monthlyPaymentLimit"]);
    const body = {
      customerId: idCustomer,
      monthlyPaymentLimit: newMonthlyPaymentLimit,
    };
    cardHolderMutation.mutate(body);
  };
  // #endregion

  // #region Yearly Payment Limit
  const newYearlyPaymentLimitSave = (newYearlyPaymentLimit) => {
    if (!newYearlyPaymentLimit) {
      console.error("No new yearly payment limit provided");
      return;
    }
    setLoadingFields((fields) => [...fields, "yearlyPaymentLimit"]);
    const body = {
      customerId: idCustomer,
      yearlyPaymentLimit: newYearlyPaymentLimit,
    };
    cardHolderMutation.mutate(body);
  };
  // #endregion

  // #region Form methods
  const { data, isLoading, isFetching, isError } = useQuery(
    ["cardview", idCustomer],
    async () => {
      const response = await HttpService.getAxiosClient().get(
        window.API_URL + `/member/card-info?customerId=${idCustomer}`
      );
      return response.data;
    }
  );

  useEffect(() => {
    if (!isFetching) {
      setLoadingFields([]);
    }
  }, [isFetching]);

  // #endregion

  // #region card buttons
  useEffect(() => {
    return () => {
      if (refreshTimeout.current) clearTimeout(refreshTimeout.current);
    };
  }, []);

  const handleRefresh = () => {
    setRefreshDisabled(true);
    queryClient.invalidateQueries(["cardview", idCustomer]);
    refreshTimeout.current = setTimeout(() => {
      setRefreshDisabled(false);
    }, 3000); // 3 seconds
  };

  useEffect(() => {
    const statusDisplay = data?.statusDisplay?.value.toLowerCase();
    if (statusDisplay) {
      switch (statusDisplay) {
        case "active":
          setAction("Lock");
          setShowDeactive(true);
          setShowActive(false);
          setShowRefresh(false);
          break;
        case "suspended":
        case "suspend":
          setAction("Unlock");
          setShowDeactive(true);
          setShowActive(false);
          setShowRefresh(false);
          break;
        case "card issued":
          setAction();
          setShowDeactive(true);
          setShowActive(true);
          setShowRefresh(false);
          break;
        case "change in progress...":
          setAction();
          setShowDeactive(false);
          setShowActive(false);
          setShowRefresh(true);
          break;
        default:
          setAction();
          setShowDeactive(false);
          setShowActive(false);
          setShowRefresh(false);
          break;
      }
    } else {
      setAction();
      setShowDeactive(false);
      setShowActive(false);
      setShowRefresh(false);
    }
  }, [data?.statusDisplay]);

  const adminCard = (action) => {
    const body = {
      customerId: idCustomer,
      status: action,
    };
    cardHolderMutation.mutate(body);
  };

  const canDeactivate = expectedName !== "" &&
    confirmationName.trim() === expectedName &&
    !cardholderQuery.isError && !cardholderQuery.isFetching &&
    !isFetching && !cardHolderMutation.isLoading &&
    showDeactive && UserService.hasRole(["pc-close-cards"]);

  const closeDeactivation = () => {
    if (cardHolderMutation.isLoading) return;
    setDeactivationOpen(false);
    setConfirmationName("");
  };

  const confirmDeactivation = () => {
    if (!canDeactivate) return;
    adminCard("CLOSE");
  };

  const cardButtons = (
    <Grid item xs={8} pt={2} alignItems="center" style={{ display: "flex" }}>
      <Box>
        {action && UserService.hasRole(["pc-lock-unlock-cards"]) && (
          <Button
            disabled={isFetching || cardHolderMutation.isLoading}
            variant="contained"
            color="primary"
            size="small"
            startIcon={action === "Lock" ? <LockIcon /> : <LockOpenIcon />}
            onClick={() => adminCard(action.toUpperCase())}
          >
            {cardHolderMutation.isLoading ? "Saving..." : action}
          </Button>
        )}
      </Box>
      {showDeactive && UserService.hasRole(["pc-close-cards"]) && (
        <Box ml={1}>
          <Button
            disabled={isFetching || cardHolderMutation.isLoading}
            variant="contained"
            color="error"
            size="small"
            startIcon={<CreditCardOffIcon />}
            onClick={() => {
              cardHolderMutation.reset();
              setConfirmationName("");
              setDeactivationOpen(true);
            }}
          >
            {cardHolderMutation.isLoading ? "Saving..." : "DEACTIVE"}
          </Button>
        </Box>
      )}
      {showActive && UserService.hasRole(["pc-activate-cards"]) && (
        <Box ml={1}>
          <Button
            disabled={isFetching || cardHolderMutation.isLoading}
            variant="contained"
            color="primary"
            size="small"
            startIcon={<PlayCircleFilledIcon />}
            onClick={() => adminCard("ACTIVATE")}
          >
            {cardHolderMutation.isLoading ? "Saving..." : "ACTIVATE"}
          </Button>
        </Box>
      )}
      {showRefresh && (
        <Box ml={1}>
          <Button
            disabled={
              refreshDisabled || cardHolderMutation.isLoading || isFetching
            }
            variant="contained"
            color="primary"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={handleRefresh}
          >
            {refreshDisabled || cardHolderMutation.isLoading || isFetching
              ? "Refreshing..."
              : "Refresh"}
          </Button>
        </Box>
      )}
    </Grid>
  );
  // #endregion

  if (isLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight={200}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isError || !data) {
    return (
      <Box p={3}>
        <Typography color="error">Failed to load card data.</Typography>
      </Box>
    );
  }

  const renderField = ({ field, ...rest }) => {
    if (!data[field]) return null;
    return (
      <Grid item xs={12} sm={6} key={field}>
        <CRMTextField
          entity="card"
          entityId={idCustomer}
          fieldName={field}
          field={data[field]}
          {...rest}
        />
      </Grid>
    );
  };

  return (
    <Fragment>
      <Dialog
        open={deactivationOpen}
        onClose={closeDeactivation}
        aria-labelledby="deactivate-card-title"
        aria-describedby="deactivate-card-description"
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle id="deactivate-card-title">
          {Labels.getLabel("manageCard.deactivateTitle", "Permanently deactivate card?")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="deactivate-card-description" sx={{ mb: 2 }}>
            {Labels.getLabel("manageCard.deactivateWarning",
              "This permanently deactivates the card. This action cannot be undone, and the card cannot be reactivated. To confirm, type the cardholder's complete name exactly as shown below.")}
          </DialogContentText>
          {cardholderQuery.isLoading || cardholderQuery.isFetching ? (
            <CircularProgress size={24} />
          ) : cardholderQuery.isError || !expectedName ? (
            <Alert severity="error">
              {Labels.getLabel("manageCard.nameUnavailable",
                "The cardholder's complete name could not be verified. Deactivation is blocked. Close this dialog and try again.")}
            </Alert>
          ) : (
            <Typography sx={{ mb: 2, fontWeight: "bold" }}>{expectedName}</Typography>
          )}
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            label={Labels.getLabel("manageCard.confirmName", "Cardholder's complete name")}
            value={confirmationName}
            onChange={(event) => setConfirmationName(event.target.value)}
            disabled={cardHolderMutation.isLoading || !expectedName}
            autoComplete="off"
            error={confirmationName !== "" && confirmationName.trim() !== expectedName}
            helperText={confirmationName !== "" && confirmationName.trim() !== expectedName
              ? Labels.getLabel("manageCard.nameMismatch", "The complete name must match exactly.")
              : " "}
          />
          {cardHolderMutation.isError && (
            <Alert severity="error">
              {Labels.getLabel("manageCard.deactivateError",
                "Card deactivation failed. Verify the card status before trying again.")}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeactivation} disabled={cardHolderMutation.isLoading}>
            {Labels.getLabel("manageCard.cancel", "Cancel")}
          </Button>
          <Button color="error" variant="contained" disabled={!canDeactivate}
            onClick={confirmDeactivation}>
            {cardHolderMutation.isLoading
              ? Labels.getLabel("manageCard.deactivating", "Deactivating...")
              : Labels.getLabel("manageCard.confirmDeactivation", "Permanently deactivate")}
          </Button>
        </DialogActions>
      </Dialog>
      <Box display="flex" flexDirection="column" height="100%">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          p={1}
          borderBottom={1}
          borderColor="divider"
        >
          <Typography variant="h6">{data?.cardNumber?.value}</Typography>
        </Box>
        <Box flex={1} p={2}>
          <Box display="flex" alignItems="flex-start">
            <CreditCardIcon sx={{ fontSize: 72, mr: 3, color: "grey.600" }} />
            <Box flex={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={12}>
                  <Box display="flex" alignItems="flex-end">
                    <Typography variant="h5" style={{ fontWeight: "bold" }}>
                      {" "}
                      {data?.statusDisplay?.value}{" "}
                    </Typography>
                    <Box
                      component="span"
                      sx={{
                        height: 24,
                        borderLeft: "1px solid #ccc",
                        mx: 1,
                        display: "inline-block",
                      }}
                    />
                    {cardButtons}
                  </Box>
                </Grid>
                {renderField({ field: "cardNumber", label: "Card Number" })}
                {renderField({
                  field: "availableBalance",
                  label: "Balance",
                  outputFormat: currencyFormat,
                })}
                {renderField({ field: "cardProxy", label: "Proxy Id" })}
                {renderField({
                  field: "monthlyPaymentLimit",
                  label: "Monthly Payment Limit",
                  outputFormat: currencyFormat,
                  inputNumericMask: inputNumericMask,
                  loading: loadingFields.includes("monthlyPaymentLimit"),
                  ...(!UserService.hasRole(["pf-only-disburse"]) && {
                    onChange: (newMonthlyPaymentLimit) =>
                      newMonthlyPaymentLimitSave(newMonthlyPaymentLimit),
                  }),
                })}
                {renderField({
                  field: "yearlyPaymentLimit",
                  label: "Yearly Payment Limit",
                  outputFormat: currencyFormat,
                  inputNumericMask: inputNumericMask,
                  loading: loadingFields.includes("yearlyPaymentLimit"),
                  ...(!UserService.hasRole(["pf-only-disburse"]) && {
                    onChange: (newYearlyPaymentLimit) =>
                      newYearlyPaymentLimitSave(newYearlyPaymentLimit),
                  }),
                })}
                {showAdminButtons && UserService.hasRole(["spend-limits"]) && (
                  <Grid item xs={12} sm={6}>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      fullWidth
                      startIcon={<SpeedIcon />}
                      onClick={handleVelocityClick}
                      sx={{ mt: 2.5 }}
                    >
                      Spend Limits
                    </Button>
                  </Grid>
                )}
                {renderField({
                  field: "accountNumber",
                  label: "Account Number",
                })}
                {renderField({
                  field: "routingNumber",
                  label: "Routing Number",
                })}
              </Grid>
            </Box>
          </Box>

          {/* Add PendingTransactions component here - only if user has pc-program-super-admin role */}
          {UserService.hasRole(["pc-program-super-admin"]) && (
            <PendingTransactions
              customerId={idCustomer}
              onRefresh={() =>
                queryClient.invalidateQueries(["cardview", idCustomer])
              }
            />
          )}
        </Box>
      </Box>
    </Fragment>
  );
}
